import { AlertTriangle, ExternalLink, FileMinus2, FilePen, FilePlus2, FileSymlink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { SKIP_REASON_LABEL } from '../../lib/format';
import type { ChangedFile, Finding, Review } from '../../types/api';
import { SeverityBadge } from '../ui/Badges';
import { EmptyState, Notice } from '../ui/Feedback';
import { DiffViewer } from './DiffViewer';

function ChangeIcon({ type }: { type: string }) {
  const cls = 'h-4 w-4 shrink-0';
  if (type === 'added') return <FilePlus2 className={cn(cls, 'text-add')} aria-label="Added" />;
  if (type === 'removed') return <FileMinus2 className={cn(cls, 'text-del')} aria-label="Removed" />;
  if (type === 'renamed' || type === 'copied') return <FileSymlink className={cn(cls, 'text-accent')} aria-label={type} />;
  return <FilePen className={cn(cls, 'text-muted')} aria-label="Modified" />;
}

export function FilesTab({ review, findings }: { review: Review; findings: Finding[] }) {
  const [selected, setSelected] = useState<string | null>(review.files[0]?.path ?? null);
  const findingsByFile = useMemo(() => {
    const m = new Map<string, Finding[]>();
    for (const f of findings) m.set(f.filePath, [...(m.get(f.filePath) ?? []), f]);
    return m;
  }, [findings]);

  if (review.files.length === 0) {
    return <EmptyState title="No changed files recorded" description="File details are stored once the review has fetched the diff." />;
  }
  const file: ChangedFile | undefined = review.files.find((f) => f.path === selected) ?? review.files[0];
  const fileFindings = file ? (findingsByFile.get(file.path) ?? []) : [];

  return (
    <div className="space-y-4">
      {review.fileListTruncated && (
        <Notice tone="warn" title="File list truncated">
          This PR changes more files than the configured MAX_FILES_PER_REVIEW. Files beyond the limit were not fetched or analyzed.
        </Notice>
      )}
      <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
        <nav aria-label="Changed files" className="card max-h-[70vh] overflow-y-auto p-1.5">
          <ul>
            {review.files.map((f) => {
              const count = findingsByFile.get(f.path)?.length ?? 0;
              const active = f.path === file?.path;
              return (
                <li key={f.path}>
                  <button
                    type="button"
                    onClick={() => setSelected(f.path)}
                    aria-current={active ? 'true' : undefined}
                    className={cn(
                      'flex w-full items-start gap-2 rounded px-2.5 py-2 text-left text-xs transition-colors',
                      active ? 'bg-chalk-blue/15 ring-1 ring-inset ring-chalk-blue/30' : 'hover:bg-raised/60',
                    )}
                  >
                    <ChangeIcon type={f.changeType} />
                    <span className="min-w-0 flex-1">
                      <span className={cn('block truncate font-mono font-medium', !f.analyzed && 'text-faint')} title={f.path}>
                        {f.path.split('/').pop()}
                      </span>
                      {f.path.includes('/') && (
                        <span className="block truncate font-mono text-2xs text-faint" title={f.path}>
                          {f.path.slice(0, f.path.lastIndexOf('/'))}
                        </span>
                      )}
                      <span className="mt-0.5 flex items-center gap-2 text-2xs">
                        <span className="text-add">+{f.additions}</span>
                        <span className="text-del">−{f.deletions}</span>
                        {!f.analyzed && <span className="text-faint">skipped</span>}
                      </span>
                    </span>
                    {count > 0 && <span className="rounded-full bg-chalk-pink/15 px-2 text-2xs font-semibold text-chalk-pink">{count}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {file && (
          <section className="card min-w-0" aria-label={`Diff for ${file.path}`}>
            <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-4 py-3">
              <ChangeIcon type={file.changeType} />
              <h3 className="min-w-0 break-all font-mono text-sm">{file.path}</h3>
              <span className="text-xs capitalize text-muted">{file.changeType}</span>
              {file.previousPath && <span className="text-xs text-faint">from {file.previousPath}</span>}
              <span className="ml-auto flex items-center gap-2 text-xs">
                <span className="text-add">+{file.additions}</span>
                <span className="text-del">−{file.deletions}</span>
                <a
                  href={`${review.prUrl}/files`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-faint hover:text-ink"
                  aria-label="Open files on GitHub"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </span>
            </header>

            {!file.analyzed && (
              <div className="border-b border-line px-4 py-2.5 text-xs text-muted">
                <AlertTriangle className="mr-1.5 inline h-3.5 w-3.5 text-warn" aria-hidden />
                Not analyzed by the reviewer: {file.skipReason ? SKIP_REASON_LABEL[file.skipReason] : 'skipped'}.
              </div>
            )}
            {file.patchTruncated && (
              <div className="border-b border-line px-4 py-2.5 text-xs text-muted">
                <AlertTriangle className="mr-1.5 inline h-3.5 w-3.5 text-warn" aria-hidden />
                Diff truncated for display. The full diff was not analyzed.
              </div>
            )}

            {fileFindings.length > 0 && (
              <ul className="space-y-1.5 border-b border-line px-4 py-3">
                {fileFindings.map((f) => (
                  <li key={f.id} className="flex flex-wrap items-center gap-2 text-xs">
                    <SeverityBadge severity={f.severity} />
                    <span className="font-medium">{f.title}</span>
                    <span className="text-faint">{f.line !== undefined ? `line ${f.line}` : 'file-level'}</span>
                  </li>
                ))}
              </ul>
            )}

            {file.patch ? (
              <DiffViewer path={file.path} patch={file.patch} findings={fileFindings} />
            ) : (
              <p className="px-4 py-8 text-center text-sm text-muted">
                No diff available for this file{file.changeType === 'removed' ? ' (deleted)' : ''}.
              </p>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
